import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import { Box, CircularProgress } from '@mui/material';

// Route-level code splitting: keep the landing Dashboard in the main bundle,
// lazy-load the chart-heavy / admin routes (Recharts, DataGrid, admin tree).
const ShowList = React.lazy(() => import('./components/ShowList'));
const ShowDetail = React.lazy(() => import('./components/ShowDetail'));
const TrendsChart = React.lazy(() => import('./components/TrendsChart'));
const Admin = React.lazy(() => import('./components/Admin'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

// Create a React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      retry: 2,
    },
  },
});

// Create MUI theme - Earthy dark theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3d7a45', // Forest green
      light: '#56a85e',
      dark: '#2b5c32',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ffffff',
      contrastText: '#131211',
    },
    background: {
      default: '#131211', // Near-black
      paper: '#1f1e1b', // Dark gray for cards
    },
    text: {
      primary: '#ffffff',
      secondary: '#a8a090',
    },
    success: {
      main: '#56a85e',
      light: '#72c07a',
    },
    warning: {
      main: '#c89030', // Deep amber/gold
      light: '#d4a040',
    },
    error: {
      main: '#c05a42', // Terracotta
      light: '#d47060',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.005em',
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#3d3b37 #1f1e1b',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#1f1e1b',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#3d3b37',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#4d4b47',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#56a85e',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#131211',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#131211',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(61, 122, 69, 0.15)',
            borderLeft: '3px solid #3d7a45',
            '&:hover': {
              backgroundColor: 'rgba(61, 122, 69, 0.25)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#191816',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: '#b0b0b0',
          '&.Mui-selected': {
            backgroundColor: 'rgba(61, 122, 69, 0.2)',
            color: '#3d7a45',
            borderColor: '#3d7a45',
            '&:hover': {
              backgroundColor: 'rgba(61, 122, 69, 0.3)',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1f1e1b',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="shows" element={<React.Suspense fallback={<RouteFallback />}><ShowList /></React.Suspense>} />
              <Route path="shows/:showName" element={<React.Suspense fallback={<RouteFallback />}><ShowDetail /></React.Suspense>} />
              <Route path="trends" element={<React.Suspense fallback={<RouteFallback />}><TrendsChart /></React.Suspense>} />
              <Route path="admin" element={<React.Suspense fallback={<RouteFallback />}><Admin /></React.Suspense>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
